export const BUDGET_SLIDERS = [
  {
    id: 'clients',
    label: 'Нужно клиентов в месяц',
    hint: 'Сколько платящих клиентов хотите получать из рекламы каждый месяц',
    min: 5,
    max: 200,
    step: 5,
    defaultValue: 20,
    format: 'number',
  },
  {
    id: 'crPct',
    label: 'Конверсия продаж (заявка → оплата)',
    hint: 'Из 10 заявок сколько заканчиваются оплатой?',
    min: 5,
    max: 80,
    step: 5,
    defaultValue: 30,
    format: 'percent',
  },
  {
    id: 'cpl',
    label: 'Средняя стоимость заявки (CPL)',
    hint: 'Сколько стоит одна заявка из рекламного кабинета. Не знаете — возьмите среднее по нише или используйте CPL-калькулятор',
    min: 500,
    max: 50000,
    step: 500,
    defaultValue: 3000,
    format: 'money',
    dividerAfter: true,
  },
  {
    id: 'check',
    label: 'Средний чек',
    hint: 'Сколько в среднем платит один клиент за одну покупку',
    min: 5000,
    max: 500000,
    step: 5000,
    defaultValue: 30000,
    format: 'money',
  },
  {
    id: 'marginPct',
    label: 'Маржинальность бизнеса',
    hint: 'Доля прибыли в выручке после всех расходов: себестоимость, зарплаты, аренда',
    min: 10,
    max: 90,
    step: 5,
    defaultValue: 50,
    format: 'percent',
    dividerAfter: true,
  },
];

export const BUDGET_FORMULA_STEPS = [
  {
    marker: '①',
    title: 'Средний чек и прибыль на продажу',
    formula: 'Средний чек = Выручка ÷ Продажи · Прибыль на продажу = (Выручка − Все расходы) ÷ Продажи',
    example: 'Пример: 350 000 ÷ 85 = <b>4 118 ₸</b> чек. (350 000 − 221 000) ÷ 85 = <b>1 518 ₸</b> прибыли с каждой продажи',
  },
  {
    marker: '②',
    title: 'CPS — сколько можно потратить на привлечение одной продажи',
    formula: 'CPS = Средний чек × Желаемая маржинальность%',
    example: 'Пример: 4 118 × 7% = <b>288 ₸</b> — максимум на маркетинг чтобы остаться с нужной маржой',
  },
  {
    marker: '③',
    title: 'CPL — стоимость лида и CPC — стоимость клика',
    formula: 'CPL = CPS × CR2 · CPC = CPL × CR1',
    example: 'Пример: 288 × 50% = <b>144 ₸</b> за лид. 144 × 5% = <b>7 ₸</b> за клик — столько может стоить клик чтобы экономика сходилась',
  },
  {
    marker: '④',
    title: 'План на следующий месяц',
    formula: 'План продаж = (Продажи ÷ Период) × 30 × (1 + Рост%) · Бюджет = План × CPL ÷ CR2',
    example: 'Пример: (85 ÷ 45) × 30 × 1.25 = <b>71 продажа</b>. 71 × 144 ÷ 50% = <b>20 448 ₸</b> рекламного бюджета',
  },
];

export const DEFAULT_BUDGET_INPUTS = BUDGET_SLIDERS.reduce((acc, slider) => {
  acc[slider.id] = slider.defaultValue;
  return acc;
}, {});

export const formatMoney = (value, locale = 'ru-RU') =>
  `${Math.round(value).toLocaleString(locale)} ₸`;

export const formatNumber = (value, locale = 'ru-RU') => Math.round(value).toLocaleString(locale);

export const formatPercent = (value) => `${Math.round(value)}%`;

export const getSliderFillPercent = (value, min, max) => {
  if (max <= min) {
    return 0;
  }

  return ((value - min) / (max - min)) * 100;
};

export const calculateBudgetMetrics = ({ clients, crPct, cpl, check, marginPct }) => {
  const cr = crPct / 100;
  const margin = marginPct / 100;
  const leads = Math.ceil(clients / cr);
  const budget = leads * cpl;
  const revenue = clients * check;
  const profit = revenue * margin;
  const net = profit - budget;
  const romi = budget > 0 ? Math.round((net / budget) * 100) : 0;
  const budgetOpt = leads * Math.round(cpl * 0.7);
  const budgetPes = leads * Math.round(cpl * 1.4);

  return {
    leads,
    budget,
    revenue,
    profit,
    net,
    romi,
    scenarios: {
      opt: budgetOpt,
      base: budget,
      pes: budgetPes,
    },
  };
};

export const getRomiBarWidth = (romi) => (Math.min(Math.max(romi, 0), 500) / 500) * 100;

export const getRomiBarColor = (romi) => {
  if (romi < 0) {
    return 'var(--accent)';
  }

  if (romi < 50) {
    return '#9a6c00';
  }

  return 'var(--green)';
};

export const getRomiStatusLabel = (romi) => {
  const key = getRomiStatusKey(romi);
  const labels = {
    ROMI_STATUS_LOSS: 'Убыток — реклама не окупается',
    ROMI_STATUS_WEAK: 'Слабая окупаемость',
    ROMI_STATUS_OK: 'Нормальная окупаемость',
    ROMI_STATUS_GOOD: 'Хорошая окупаемость',
    ROMI_STATUS_GREAT: 'Отличная окупаемость',
  };
  return labels[key] || '';
};

export const getRomiStatusKey = (romi) => {
  if (romi < 0) {
    return 'ROMI_STATUS_LOSS';
  }

  if (romi < 50) {
    return 'ROMI_STATUS_WEAK';
  }

  if (romi < 150) {
    return 'ROMI_STATUS_OK';
  }

  if (romi < 300) {
    return 'ROMI_STATUS_GOOD';
  }

  return 'ROMI_STATUS_GREAT';
};

export const getInsightState = ({ clients, crPct, cpl, check, marginPct, net, romi, profit, budget }) => {
  if (net < 0) {
    return {
      variant: 'bad',
      message: 'Бизнес убыточен при текущих параметрах: расходы <b>{totalCost}</b> превышают выручку <b>{revenue}</b>. Нужно снизить себестоимость или поднять цену.',
      vars: { profit, budget },
    };
  }

  if (romi < 50) {
    return {
      variant: 'warn',
      message: 'ROMI <b>{romi}%</b> — реклама окупается, но слабо. Чистая прибыль <b>{net}</b>. Попробуйте улучшить конверсию отдела продаж или оффер — это дешевле чем снижать CPL.',
      vars: { romi, net },
    };
  }

  if (crPct < 20) {
    const margin = marginPct / 100;
    const betterNet = Math.round(clients * check * margin - Math.ceil(clients / 0.3) * cpl);

    return {
      variant: 'warn',
      message: 'Конверсия продаж <b>{crPct}%</b> — слабое место. Из 10 заявок покупает только <b>{buyers}</b>. Скрипт, скорость ответа и квалификация лида дадут больший прирост, чем рост бюджета.',
      vars: { crPct, betterNet },
    };
  }

  return {
    variant: 'good',
    message: 'Хорошая модель. Запас прочности <b>{safety}</b> — даже при падении продаж на треть бизнес остаётся в плюсе. Для прибыли <b>{goal}</b> нужно <b>{salesNeeded} продаж</b> ({salesDaily}/день).',
    vars: {
      returnPerTenge: (romi / 100 + 1).toFixed(1),
      net,
    },
  };
};
